export default (entangled, config, errorIndices = []) => ({
    model: entangled,
    pills: [],
    inputValue: '',
    dropdownOpen: false,
    activeIndex: -1,
    readonly: config.readonly ?? false,
    disabled: config.disabled ?? false,

    get editable() {
        // A replaced wire:ignore widget must not commit a delayed blur.
        return !this.readonly && !this.disabled && this.$el?.isConnected !== false;
    },

    get errorState() {
        return this.pills.some(p => p.invalid);
    },

    get filteredSuggestions() {
        if (!config.suggestions.length) return [];
        const term = this.inputValue.toLowerCase();
        return term
            ? config.suggestions.filter(s => s.toLowerCase().includes(term))
            : config.suggestions;
    },

    _uid() {
        return Math.random().toString(36).slice(2);
    },

    init() {
        const initial = Array.isArray(this.model) ? this.model : [];
        this.pills = initial.map((v, i) => ({
            value: v,
            invalid: errorIndices.includes(i),
            editing: false,
            _id: this._uid(),
        }));

        this.$watch('model', (newVal) => {
            const incoming = Array.isArray(newVal) ? newVal : [];
            if (JSON.stringify(incoming) === JSON.stringify(this.pills.map(p => p.value))) return;
            if (this.pills.some(p => p.editing)) return;
            this.pills = incoming.map((v, i) => ({
                value: v,
                invalid: errorIndices.includes(i),
                editing: false,
                _id: this._uid(),
            }));
        });

        this.$watch('inputValue', (value) => {
            if (this.editable && config.suggestions.length > 0 && value.length > 0) {
                this.dropdownOpen = this.filteredSuggestions.length > 0;
                this.activeIndex = -1;
            } else {
                this.dropdownOpen = false;
            }
        });
    },

    syncModel() {
        if (!this.editable) return;
        this.model = this.pills.map(p => p.value);
    },

    commitItem(raw) {
        if (!this.editable) return false;
        let value = config.trimItems ? raw.trim() : raw;

        if (!value) return false;

        if (config.lowercase) {
            value = value.toLowerCase();
        }

        if (config.enforceSuggestions && config.suggestions.length > 0 && !config.suggestions.includes(value)) {
            return false;
        }

        if (config.max !== null && this.pills.length >= config.max) {
            return false;
        }

        if (config.unique && this.pills.some(p => p.value === value)) {
            return false;
        }

        this.pills = [...this.pills, { value, invalid: false, editing: false, _id: this._uid() }];
        this.syncModel();
        return true;
    },

    startEdit(index) {
        if (!this.editable) return;
        this.pills = this.pills.map((p, i) => ({
            ...p,
            editing: i === index,
            invalid: i === index ? false : p.invalid,
        }));
        const uid = this.pills[index]?._id;
        if (uid) {
            this.$nextTick(() => {
                const el = document.getElementById('pill-edit-' + uid);
                el?.focus();
                el?.select();
            });
        }
    },

    finishEdit(index, rawValue) {
        if (!this.editable) return;
        if (index >= this.pills.length || !this.pills[index]?.editing) return;

        let value = config.trimItems ? rawValue.trim() : rawValue;
        if (config.lowercase) value = value.toLowerCase();

        this.pills = this.pills.map((p, i) => i === index
            ? { ...p, value: value !== '' ? value : p.value, editing: false }
            : p
        );

        this.syncModel();
        this.$refs.pillInput?.focus();
    },

    cancelEdit(index) {
        if (index >= this.pills.length || !this.pills[index]?.editing) return;
        this.pills = this.pills.map((p, i) => i === index ? { ...p, editing: false } : p);
        this.$refs.pillInput?.focus();
    },

    commitInput() {
        if (!this.editable) return;
        this.commitItem(this.inputValue);
        this.inputValue = '';
        this.dropdownOpen = false;
    },

    handleKeyDown(event) {
        if (!this.editable) return;
        if (event.key === 'Enter') {
            event.preventDefault();
            if (this.dropdownOpen && this.activeIndex >= 0) {
                this.selectSuggestion(this.filteredSuggestions[this.activeIndex]);
            } else {
                this.commitInput();
            }
            return;
        }

        if (config.separators.includes(event.key)) {
            event.preventDefault();
            this.commitInput();
            return;
        }

        if (event.key === 'Backspace' && this.inputValue === '') {
            this.removeLast();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveIndex(1);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveIndex(-1);
            return;
        }

        if (event.key === 'Escape') {
            this.dropdownOpen = false;
            this.activeIndex = -1;
        }
    },

    handlePaste(event) {
        if (!this.editable) return;
        event.preventDefault();
        const text = (event.clipboardData || window.clipboardData).getData('text');
        const sepPattern = '[' + config.separators.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('') + '\n\r]';
        text.split(new RegExp(sepPattern)).forEach(item => this.commitItem(item));
    },

    removeItem(index) {
        if (!this.editable) return;
        this.pills = this.pills.filter((_, i) => i !== index);
        this.syncModel();
    },

    removeLast() {
        if (!this.editable) return;
        if (this.pills.length > 0) {
            this.pills = this.pills.slice(0, -1);
            this.syncModel();
        }
    },

    selectSuggestion(value) {
        if (!this.editable) return;
        this.inputValue = value;
        this.commitInput();
        this.$refs.pillInput?.focus();
    },

    moveIndex(delta) {
        const count = this.filteredSuggestions.length;
        if (count === 0) return;
        this.activeIndex = ((this.activeIndex + delta) % count + count) % count;
    },

    focusInput() {
        this.$refs.pillInput?.focus();
    },

    closeDropdown(event) {
        if (!this.$el.contains(event.target)) {
            this.dropdownOpen = false;
            this.activeIndex = -1;
        }
    },
})
